/**
 * ShowPage — single-show page for /show/:slug.
 *
 * Shows the show's hero image/banner, title, tagline, channel + schedule
 * info, and an episode list pulled from videos tagged with show_slug.
 * Falls back to a neat "coming soon" empty state when the show has no
 * videos yet.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Play, Calendar, Tv } from "lucide-react";
import api from "../lib/api";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function fmtHour(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const d = new Date(); d.setHours(hh, mm, 0, 0);
  return fmtTime(d.toISOString());
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ShowPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/tv-guide/shows/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (!data?.show) {
    return (
      <div data-testid="show-page-missing" className="pt-32 px-6 text-center max-w-md mx-auto">
        <Tv className="w-12 h-12 mx-auto text-white/30 mb-3" />
        <div className="font-display font-black text-2xl">Show not found</div>
        <div className="text-white/50 mt-1 text-sm">"{slug}" isn't on the schedule.</div>
        <Link to="/tv-guide" className="inline-block mt-4 bg-red-600 hover:bg-red-500 text-white font-bold px-5 py-2 rounded-lg">Back to TV Guide</Link>
      </div>
    );
  }

  const show = data.show;
  const episodes = data.episodes || [];
  const days = WEEKDAYS.filter((_, i) => (show.weekday_mask || 0b1111111) & (1 << i));

  return (
    <div data-testid={`show-page-${slug}`} className="min-h-screen bg-black text-white">
      {/* Hero */}
      <div className="relative aspect-[21/9] sm:aspect-[5/2] lg:aspect-[3/1] overflow-hidden">
        {show.hero_url ? (
          <img src={show.hero_url} alt={show.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${show.accent_color || "#EF4444"}55, #000 70%)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        <div className="absolute top-4 left-4">
          <button data-testid="show-back-btn" onClick={() => nav(-1)} className="bg-black/60 hover:bg-black/80 text-white inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black mb-2" style={{ color: show.accent_color || "#EF4444" }}>
            CH {show.channel_number} · TAP IN {(show.category || "").toUpperCase()}
          </div>
          <h1 data-testid="show-title" className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tight">{show.title}</h1>
          <p data-testid="show-tagline" className="text-white/80 mt-3 text-base max-w-xl">{show.tagline || show.description}</p>
          <div className="mt-4 flex items-center gap-3 text-xs text-white/70">
            <Calendar className="w-3.5 h-3.5" />
            {days.length === 7 ? "Daily" : days.join(" · ")} ·{" "}
            <span className="font-bold text-white">{fmtHour(show.recurring_start_h)} – {fmtHour(show.recurring_end_h)}</span>
            <span className="text-white/40">(your local time)</span>
          </div>
        </div>
      </div>

      {/* Episodes */}
      <section className="px-4 sm:px-8 lg:px-16 py-10 max-w-7xl mx-auto">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display font-black text-xl uppercase tracking-[0.2em]">Episodes</h2>
          <span className="text-white/50 text-xs">{episodes.length} available</span>
        </div>

        {episodes.length === 0 ? (
          <div data-testid="show-empty-episodes" className="bg-white/[0.02] border border-dashed border-white/15 rounded-2xl py-16 px-8 text-center">
            <Tv className="w-10 h-10 mx-auto text-white/30 mb-3" />
            <div className="font-display font-black text-xl">New episodes coming soon</div>
            <div className="text-white/50 text-sm mt-1 max-w-sm mx-auto">
              Catch <span className="text-white font-bold">{show.title}</span> live on Channel {show.channel_number} at {fmtHour(show.recurring_start_h)}, or follow the show to be notified the moment a new episode drops.
            </div>
            <Link to="/tv-guide" className="inline-block mt-5 bg-red-600 hover:bg-red-500 text-white font-bold px-5 py-2 rounded-lg text-sm">Back to TV Guide</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {episodes.map((ep) => (
              <Link
                key={ep.id}
                to={`/watch/${ep.id}`}
                data-testid={`show-episode-${ep.id}`}
                className="group rounded-xl overflow-hidden border border-white/10 hover:border-red-500/50 transition"
              >
                <div className="aspect-video bg-black relative">
                  {ep.thumbnail_url
                    ? <img src={ep.thumbnail_url} alt={ep.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition" />
                    : <div className="absolute inset-0 bg-gradient-to-br from-red-900 to-black" />}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white fill-current" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-bold text-white text-sm line-clamp-2">{ep.title}</div>
                  <div className="text-white/50 text-xs mt-1 flex gap-2">
                    {ep.view_count != null && <span>{Number(ep.view_count).toLocaleString()} views</span>}
                    {ep.duration_sec ? <span>· {Math.floor(ep.duration_sec / 60)}m</span> : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
